/**
 * The renderers Kronos currently ships. This is the single source of truth for
 * "which renderer values are valid", the render switch, the default policy, and
 * the settings-load sanitizer all derive from it, so removing a renderer only
 * requires editing this list.
 */
export type RendererType = 'phylocanvas' | 'theiaphyloviewer';

export const SUPPORTED_RENDERERS: readonly RendererType[] = ['phylocanvas', 'theiaphyloviewer'];

/**
 * Probe whether the current runtime can create a usable WebGL context.
 *
 * Used as a backstop for renderer-default selection: some embedded browsers
 * cannot drive phylocanvas.gl's WebGL pipeline, so we fall back to SVG when no
 * context is available. Returns `true` when there is no DOM to probe (Node /
 * SSR / tests): we cannot disprove WebGL there, so we assume the capable default
 * and let other signals (e.g. Tableau `environment.context`) decide.
 */
export function hasReliableWebGL(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    return gl != null;
  } catch {
    return false;
  }
}

/**
 * Pure renderer-default policy. `context` is the Tableau host-context string
 * (`undefined` for the standalone app); `webglOk` is the WebGL probe result.
 *
 * Tableau Desktop (and Public Desktop) always gets the SVG renderer because its
 * embedded CEF browser cannot be trusted with WebGL even when a context object
 * is returned. Every other host gets WebGL when the probe passes, SVG otherwise.
 */
export function chooseDefaultRenderer(
  context: string | undefined,
  webglOk: boolean,
): RendererType {
  const ctx = String(context ?? '').toLowerCase();
  if (ctx === 'desktop' || ctx === 'public-desktop') {
    return 'theiaphyloviewer';
  }
  return webglOk ? 'phylocanvas' : 'theiaphyloviewer';
}

/**
 * Coerce a persisted/selected renderer value to a currently-supported one.
 *
 * Settings may carry a value from a removed renderer (e.g. the legacy `simple`
 * SVG renderer) or be absent entirely. Anything not in `SUPPORTED_RENDERERS`
 * collapses to `fallback`, which callers pass as the host-aware default.
 */
export function sanitizeRendererType(
  value: string | null | undefined,
  fallback: RendererType,
): RendererType {
  return SUPPORTED_RENDERERS.includes(value as RendererType) ? (value as RendererType) : fallback;
}

/**
 * Whether a renderer ships its own embedded appearance UI, and therefore should
 * NOT also receive the shared standalone appearance panel.
 *
 * Phylocanvas.gl carries a built-in gear-menu with the full layout / label /
 * scalebar controls, so the standalone app must not draw a second, competing
 * panel for it. Every other renderer (the SVG `theiaphyloviewer`, plus any
 * future addition) has no embedded menu, so the shared panel is shown instead.
 * Defaulting unknown renderers to `false` is the safe choice: a new renderer
 * gets the shared panel rather than silently losing all appearance controls.
 */
export function rendererProvidesOwnAppearanceUI(type: RendererType): boolean {
  return type === 'phylocanvas';
}

/**
 * Whether a renderer draws its own metadata blocks (heat-map columns) from the
 * settings bag, and therefore should NOT also receive app-layer block dispatch.
 *
 * Phylocanvas.gl computes and renders metadata blocks itself through its embedded
 * gear-menu, so the app must not push a second, competing set. The SVG
 * `theiaphyloviewer` has the block-drawing setters but no embedded menu, its
 * blocks stay dark until the app routes computed blocks to it, so it returns
 * `false`. Defaulting unknown renderers to `false` is the safe choice: a new
 * renderer gets app-routed blocks rather than silently showing none.
 */
export function rendererProvidesOwnMetadataUI(type: RendererType): boolean {
  return type === 'phylocanvas';
}
